import React from 'react';

import API from 'utils/api/api';
import Logger from 'utils/logger';
import { SearchRequest, UuidableName } from 'utils/api/types';
import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import Chip from 'components/Chip';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import { StyledTextField } from 'components/Input/styles';

const PLACEHOLDER_NAME = 'Loading…';

type FormItem = { key: string; value?: string };

interface FormsSearchFieldProps {
  value: FormItem[];
  onChange: (items: { key: string; value: string }[]) => void;
  error?: boolean;
  helperText?: string;
}

const searchEventForms = (params?: SearchRequest): ReturnType<typeof API.entities.searchForms> =>
  API.entities.searchForms('events', params);

const FormsSearchField = ({ value, onChange, error, helperText }: FormsSearchFieldProps): React.ReactElement => {
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const [resolvedNames, setResolvedNames] = React.useState<Record<string, string>>({});
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(
    searchEventForms,
    shouldLoad
  );

  const unresolved = value.filter(({ key, value: name }) => !name && !resolvedNames[key]).map(({ key }) => key);
  const unresolvedKey = unresolved.join(',');
  const unresolvedSet = new Set(unresolved);
  const selected: UuidableName[] = value.map(({ key, value: name }) => ({
    uuid: key,
    name: name || resolvedNames[key] || PLACEHOLDER_NAME,
  }));

  React.useEffect(() => {
    if (!unresolvedKey) {
      return undefined;
    }
    let cancelled = false;
    API.entities
      .searchForms('events', { uuid: unresolvedKey, limit: unresolvedKey.split(',').length })()
      .then(({ data }) => {
        if (!cancelled) {
          setResolvedNames((previous) => ({
            ...previous,
            ...Object.fromEntries(data.map(({ uuid, name }) => [uuid, name])),
          }));
        }
      })
      .catch((requestError) => Logger.warn('Could not load the selected forms', requestError));
    return () => {
      cancelled = true;
    };
  }, [unresolvedKey]);

  return (
    <InfiniteAutocomplete
      multiple
      disableCloseOnSelect
      fullWidth
      size="medium"
      id="multi-drop-down-field"
      data-testid="Field description"
      options={results}
      value={selected}
      loading={loading}
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      filterSelectedOptions
      onOpen={() => setShouldLoad(true)}
      onChange={(_, newValue) =>
        onChange(newValue.map(({ uuid, name }) => ({ key: uuid, value: unresolvedSet.has(uuid) ? '' : name })))
      }
      onInputChange={(_, newInputValue, reason) => searchQuery(reason === 'input' ? newInputValue : '')}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selectedOption) => option.uuid === selectedOption.uuid}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} label={option.name} fontSize={14} variant="filled" />
        ))
      }
      renderInput={(params) => (
        <StyledTextField
          {...params}
          error={error}
          helperText={helperText}
          placeholder={value.length > 0 ? '' : 'Choose option(s)'}
        />
      )}
    />
  );
};

export default FormsSearchField;
